<template>
  <div class="w-full flex justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4 min-h-fit">
      <div class="flex justify-between">
        <Button color="normal" class="w-12 h-6" size="sm" @click="emit('cancel')">
          {{ $t('common.backButton') }}
        </Button>
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="isCanEnabled"
          :disabled="!isCanEnabled"
          @click="handleTakeEffect"
        >
          {{ $t('keys.rs.apply') }}
        </Button>
      </div>

      <div class="flex-1 flex flex-col justify-center items-center">
        <div class="flex items-center gap-16">
          <div
            v-for="(slot, slotIndex) in rsSlots"
            :key="slot.value"
            :class="`relative flex flex-col items-center ${selectedSlot === slot.value ? 'text-text-important-default' : 'text-text-body-default'}`"
          >
            <div
              :class="`flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all hover:border-blue-light-hover ${getSlotBorderClass(slot.value)}`"
              @click="selectedSlot = slot.value"
            >
              <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
                {{ getBoundKeyLabel(currentSelection[slot.value]) }}
              </span>
            </div>
            <span class="text-m-medium text-black-white">{{ slot.label }}</span>
            <Icon
              v-show="currentSelection[slot.value]"
              icon="Function-Fill-Close"
              class="absolute -top-1 -right-1 w-4 h-4 text-brand-red cursor-pointer"
              @click.stop="clearSlot(slot.value)"
            />
            <Icon
              v-if="slotIndex === 0"
              icon="Function-Wireframe-HorizontalBind"
              class="absolute top-4 -right-11 w-6 h-6 text-black-white pointer-events-none"
            />
          </div>
        </div>
      </div>

      <div class="flex items-center text-text-glass-default">
        <Icon icon="Function-FiII-Description" class="size-4" />
        <span class="text-s-medium">{{ $t('keys.advanced.bindHint') }}</span>
      </div>
    </div>

    <div class="flex flex-col glass-frosted relative rounded-8xl w-250 h-67.5 p-4 min-h-fit">
      <div class="p-4 flex flex-col gap-1 h-20 shrink-0 bg-key rounded-4xl">
        <div class="flex items-center justify-between">
          <div class="flex items-center text-black-white">
            <Icon icon="Technology-Wireframe-QuickTrigger" class="w-6 h-6" />
            <span class="text-m-medium">{{ $t('keys.rs.quickTrigger') }}</span>
          </div>
          <Switch v-model="rtEnabled" />
        </div>
        <p class="text-text-glass-default text-s-medium">
          {{ $t('keys.rs.quickTriggerDescription') }}
        </p>
      </div>
      <div class="p-4 mt-2 flex-1 flex flex-col gap-1 bg-key rounded-4xl">
        <div class="flex items-center text-black-white">
          <Icon icon="Function-Wireframe-CurrentClick" class="w-6 h-6" />
          <span class="text-m-medium">{{ $t('keys.rs.executePoint') }}</span>
        </div>
        <p class="text-text-glass-default text-s-medium">
          {{ $t('keys.rs.executePointDescription') }}
        </p>
        <div class="flex items-start gap-3 w-full">
          <span class="text-black-white text-s-medium shrink-0">{{ MIN }}</span>

          <div class="relative flex-1 flex items-start h-16">
            <div class="relative flex-1 h-5 flex items-center">
              <Slider
                v-model="value"
                :min="SLIDE_MIN"
                :max="SLIDE_MAX"
                :step="1"
                class="w-full"
                track-class="bg-glass30"
                range-class="bg-brand-blue1 disabled:bg-disabled"
                thumb-class="bg-white"
              />
              <div
                class="absolute top-full left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: bubbleLeft }"
              >
                <div class="text-brand-blue1 text-l-medium">
                  {{ currentValue }}
                </div>
                <div class="text-text-glass-default text-s-medium">(~{{ mmValue }}mm)</div>
              </div>
            </div>
          </div>

          <span class="text-black-white text-s-medium shrink-0">{{ MAX }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { Button, Icon, Slider, Switch, toast } from '@rapoo/element'
  import type { KeyConfig } from '@rapoo/element'
  import type { RS } from '@rapoo/device-sdk'
  import { useLocaleStore } from '@/stores'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    buildKeySettingFromPayload,
    isRemapRestrictedKey,
  } from '../../../utils/keyMapSettings'
  import {
    GCOME_MATRIX_TYPE,
    buildRsMtSlotsFromKeys,
    applyRsSocdPreferredSlotIndexes,
    collectRsSocdRestoreTargets,
    resolveRsSocdPairContext,
  } from '../../../utils/advancedKeyMapSettings'
  import {
    AdvancedEditingSettingSymbol,
    SelectedKeySymbol,
    KeySettingsSymbol,
    KeyLayerSymbol,
    type KeyChangePayload,
    type KeySetting,
  } from '../../../types'
  import { findSettingByTargetKey, parseNumber } from '../../../utils/keyIdentity'
  import { useAdvancedKeyCommit } from '../composables/useAdvancedKeyCommit'
  import { usePhysicalKeyPressCapture } from '../composables/usePhysicalKeyPressCapture'

  const emit = defineEmits<{
    success: []
    cancel: []
  }>()

  type SlotType = 'key1' | 'key2'

  const MIN = 0
  const MAX = 255
  const SLIDE_MIN = 0
  const SLIDE_MAX = 255
  const THUMB_SIZE = 12
  const DEFAULT_SLIDER_VALUE = 37
  const THRESHOLD_MM_MIN = 0.01
  const THRESHOLD_MM_MAX = 3.4
  const THRESHOLD_MM_RANGE = THRESHOLD_MM_MAX - THRESHOLD_MM_MIN
  const APPLY_TOAST_ID = 'advanced-rs-apply'

  const sliderToMm = (slider: number) => THRESHOLD_MM_MIN + (slider / MAX) * THRESHOLD_MM_RANGE

  const { t } = useLocaleStore()
  const { commitAdvancedKey, restoreAdvancedKey } = useAdvancedKeyCommit()

  const selectedKey = inject(SelectedKeySymbol)
  const editingSetting = inject(AdvancedEditingSettingSymbol, ref<KeySetting | null>(null))
  const keySettings = inject(KeySettingsSymbol)
  const currentLayer = inject(KeyLayerSymbol)

  const selectedSlot = ref<SlotType>('key1')
  const currentSelection = ref<Record<SlotType, KeyConfig | null>>({
    key1: null,
    key2: null,
  })
  const rtEnabled = ref(false)
  const value = ref([DEFAULT_SLIDER_VALUE])
  const isSubmitting = ref(false)
  const suppressApplyToast = ref(false)

  const rsSlots = computed<{ label: string; value: SlotType }[]>(() => [
    { label: t('keys.rs.key1'), value: 'key1' },
    { label: t('keys.rs.key2'), value: 'key2' },
  ])

  const currentValue = computed(() => value.value[0] ?? SLIDE_MIN)
  const mmValue = computed(() => sliderToMm(currentValue.value).toFixed(2))
  const triggerThreshold = computed(() => Math.round(sliderToMm(currentValue.value) * 100))

  const sliderProgress = computed(() => {
    const ratio = (currentValue.value - SLIDE_MIN) / (SLIDE_MAX - SLIDE_MIN)
    return Math.min(1, Math.max(0, ratio))
  })

  const bubbleLeft = computed(() => {
    const progress = sliderProgress.value
    const offset = (0.5 - progress) * THUMB_SIZE
    return `calc(${progress * 100}% + ${offset}px)`
  })

  const isCanEnabled = computed(() =>
    Boolean(currentSelection.value.key1 && currentSelection.value.key2 && !isSubmitting.value),
  )

  const getBoundKeyLabel = (key: KeyConfig | null) => {
    if (!key) return ''
    return getLocalizedField(key.alias || key.name)
  }

  const getSlotBorderClass = (slot: SlotType) => {
    if (selectedSlot.value === slot) return 'border-brand-blue1'
    return 'border-transparent'
  }

  const parseSliderValueByThreshold = (threshold: unknown) => {
    const parsed =
      typeof threshold === 'number' ? threshold : Number.parseInt(String(threshold ?? ''), 10)
    if (Number.isNaN(parsed)) return DEFAULT_SLIDER_VALUE
    const clamped = Math.min(THRESHOLD_MM_MAX * 100, Math.max(THRESHOLD_MM_MIN * 100, parsed))
    const slider = Math.round(((clamped / 100 - THRESHOLD_MM_MIN) / THRESHOLD_MM_RANGE) * MAX)
    return Math.min(MAX, Math.max(MIN, slider))
  }

  const toBoundKey = (keywords: string, index?: number): KeyConfig | null => {
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)
    if (!config) return null
    return { ...config, key: index as number }
  }

  const restorePairFromSetting = (setting: KeySetting) => {
    const key1Index = parseNumber(setting.raw?.index)
    const key2Index = parseNumber(setting.raw?.relatedKeyIndex ?? setting.raw?.rs?.relatedKeyIndex)
    const key2Keywords = setting.raw?.relatedKeyKeywords as string | undefined

    currentSelection.value = {
      key1: toBoundKey(setting.keywords, key1Index),
      key2: key2Keywords ? toBoundKey(key2Keywords, key2Index) : null,
    }

    const rs = setting.raw?.rs as RS | undefined
    rtEnabled.value = rs?.combineRT === 1
    value.value = [parseSliderValueByThreshold(rs?.triggerThreshold)]
    selectedSlot.value = currentSelection.value.key2 ? 'key1' : 'key2'
  }

  const assignToSlot = (slot: SlotType, key: KeyConfig) => {
    const other = slot === 'key1' ? currentSelection.value.key2 : currentSelection.value.key1
    if (other?.keywords && other.keywords === key.keywords) {
      toast.info(t('toast.keys.rsSameKey'))
      return
    }

    currentSelection.value[slot] = key
    if (slot === 'key1' && !currentSelection.value.key2) selectedSlot.value = 'key2'
    else if (slot === 'key2' && !currentSelection.value.key1) selectedSlot.value = 'key1'
  }

  const clearSlot = (slot: SlotType) => {
    currentSelection.value[slot] = null
    selectedSlot.value = slot
  }

  const handlePhysicalKeyPress = (key: KeyConfig) => {
    if (isRemapRestrictedKey(key.keywords)) {
      toast.info(t('toast.keys.fnNotSupported'))
      return
    }
    assignToSlot(selectedSlot.value, key)
  }

  usePhysicalKeyPressCapture({
    enabled: () => !isSubmitting.value,
    onKeyPress: handlePhysicalKeyPress,
  })

  watch(
    editingSetting,
    (setting) => {
      if (setting?.keyType === 'RS') {
        restorePairFromSetting(setting)
      }
    },
    { immediate: true },
  )

  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key) return

      assignToSlot(selectedSlot.value, key)
    },
  )

  watch(
    editingSetting,
    () => {
      suppressApplyToast.value = false
    },
    { immediate: true },
  )

  watch(isCanEnabled, (enabled, wasEnabled) => {
    if (suppressApplyToast.value) return
    if (enabled && !wasEnabled) {
      toast.info(t('toast.keys.rsApply'), { id: APPLY_TOAST_ID })
    }
  })

  const resolveKeyIndex = (key: KeyConfig) => {
    const existing = keySettings?.value ? findSettingByTargetKey(keySettings.value, key) : undefined
    return parseNumber(existing?.raw?.index) ?? parseNumber(key.key)
  }

  const handleTakeEffect = async () => {
    const key1 = currentSelection.value.key1
    const key2 = currentSelection.value.key2
    if (!key1 || !key2) {
      toast.info(t('toast.keys.rsSelect'))
      return
    }

    const index1 = resolveKeyIndex(key1)
    const index2 = resolveKeyIndex(key2)
    if (index1 == null || index2 == null) return

    const { existingSetting, peerExistingSetting, originalPairSettings, editingPeer } =
      resolveRsSocdPairContext({
        key1,
        key2,
        keySettings: keySettings?.value,
        editingSetting: editingSetting?.value,
      })

    const { mt, peerMt } = buildRsMtSlotsFromKeys({ key1, key2 })
    const rs: RS = {
      combineRT: rtEnabled.value ? 1 : 0,
      relatedKeyIndex: index2,
      triggerKeyType: 0x00,
      triggerKeyCode: parseNumber(key1.code) || 0,
      triggerKeyExtCode: 0x00,
      triggerThreshold: triggerThreshold.value,
    }

    const payload: KeyChangePayload = {
      keywords: key1.keywords,
      keyType: 'RS',
      target: null,
      raw: {
        type: GCOME_MATRIX_TYPE.RS,
        mt,
        peerMt,
        relatedKeyIndex: index2,
        relatedKeyKeywords: key2.keywords,
        relatedKeyLabel: getBoundKeyLabel(key2),
        peerKeyIndex: index2,
        originalLabel: getBoundKeyLabel(key1),
        rs,
      },
    }

    const newSetting = buildKeySettingFromPayload({
      sourceData: payload,
      index: index1,
    })
    if (!newSetting) return

    applyRsSocdPreferredSlotIndexes(newSetting, {
      existingSetting,
      peerExistingSetting,
      editingSetting: editingSetting?.value,
      editingPeer,
    })

    let didSucceed = false
    suppressApplyToast.value = true
    toast.dismiss(APPLY_TOAST_ID)
    isSubmitting.value = true
    try {
      const committed = await commitAdvancedKey({
        setting: newSetting,
        existingSetting,
        peerExistingSetting,
        isFn: currentLayer?.value === 'fn',
      })
      if (!committed) {
        suppressApplyToast.value = false
        return
      }

      await restoreReplacedPairKeys({
        key1,
        key2,
        originalPairSettings,
        existingSetting,
        peerExistingSetting,
        reusedSlotIndexes: [newSetting.raw?.mtIndex, newSetting.raw?.peerMtIndex],
      })

      toast.dismiss('keyboard')
      emit('success')
      didSucceed = true
    } finally {
      if (!didSucceed) isSubmitting.value = false
    }
  }

  const restoreReplacedPairKeys = async ({
    key1,
    key2,
    originalPairSettings,
    existingSetting,
    peerExistingSetting,
    reusedSlotIndexes,
  }: {
    key1: KeyConfig
    key2: KeyConfig
    originalPairSettings: KeySetting[]
    existingSetting?: KeySetting
    peerExistingSetting?: KeySetting
    reusedSlotIndexes: unknown[]
  }) => {
    const reusedSlots = new Set(
      reusedSlotIndexes
        .map((index) => parseNumber(index))
        .filter((index): index is number => index != null),
    )

    const targets = collectRsSocdRestoreTargets({
      keepKeywords: [key1.keywords, key2.keywords],
      keySettings: keySettings?.value,
      originalPairSettings,
      existingSetting,
      peerExistingSetting,
      keyType: 'RS',
    })

    for (const leftover of targets) {
      const leftoverSlot = parseNumber(leftover.raw?.mtIndex)
      const defaultKey = toBoundKey(leftover.keywords, parseNumber(leftover.raw?.index))
      if (!defaultKey) continue

      await restoreAdvancedKey({
        setting: leftover,
        defaultKey,
        isFn: currentLayer?.value === 'fn',
        clearSlot: leftoverSlot == null || !reusedSlots.has(leftoverSlot),
      })
    }
  }
</script>

<style scoped></style>
